/**
 * Phase types for the Needs Map
 */

/**
 * Phase represents a category or stage for topic nodes
 */
export interface Phase {
  id: string;
  name: string;
  description: string;
  color: string;
}

/**
 * PhaseMap is a record of phases indexed by their IDs
 */
export type PhaseMap = Record<string, Phase>;

/**
 * PhaseNodeData for ReactFlow phase nodes
 */
export interface PhaseNodeData {
  id: string;
  name: string;
  description: string;
  color: string;
  childCount: number; // Number of child nodes in this phase
  width?: number;
  height?: number;
}

/**
 * Edge type for phase edges
 */
export type PhaseEdgeType = 'phase-flow' | 'phase-relation';

/**
 * Edge strength type
 */
export type EdgeStrength = 'weak' | 'medium' | 'strong';

/**
 * PhaseEdgeData for ReactFlow phase edges
 */
export interface PhaseEdgeData {
  id: string;
  color: string;
  type: PhaseEdgeType;
  strength?: EdgeStrength;
}

/**
 * PhaseLayoutConfig for configuring phase layout
 */
export interface PhaseLayoutConfig {
  showPhaseNodes: boolean;
  showPhaseEdges: boolean;
  phaseNodeSpacing: number;
  childNodeSpacing: number;
}

/**
 * Get a phase by ID from a list of phases
 */
export function getPhaseById(phaseId: string, phases: Phase[]): Phase | undefined {
  return phases.find(phase => phase.id === phaseId);
}

/**
 * Get the color for a phase
 */
export function getPhaseColor(phaseId: string, phases: Phase[] = []): string {
  const phase = getPhaseById(phaseId, phases);
  return phase?.color || "#cccccc";
}

/**
 * Get the name for a phase
 */
export function getPhaseName(phaseId: string, phases: Phase[] = []): string {
  const phase = phases.find(p => p.id === phaseId);
  return phase?.name || "未分類";
}

/**
 * Convert phases array to a map
 */
export function phasesToMap(phases: Phase[]): PhaseMap {
  return phases.reduce((map, phase) => {
    map[phase.id] = phase;
    return map;
  }, {} as PhaseMap);
}

/**
 * Create a node ID for a phase node
 */
export function createPhaseNodeId(phaseId: string): string {
  return `phase-${phaseId}`;
}

/**
 * Check if a node ID is a phase node
 */
export function isPhaseNodeId(nodeId: string): boolean {
  return nodeId.startsWith('phase-');
}

/**
 * Extract phase ID from a phase node ID
 */
export function extractPhaseIdFromNodeId(phaseNodeId: string): string | null {
  if (isPhaseNodeId(phaseNodeId)) {
    return phaseNodeId.replace('phase-', '');
  }
  return null;
}

/**
 * Create a phase edge ID
 */
export function createPhaseEdgeId(sourcePhaseId: string, targetPhaseId: string): string {
  return `phase-edge-${sourcePhaseId}-${targetPhaseId}`;
}

/**
 * Generate phase flow edges
 * Creates edges that show the natural progression between phases
 */
export function generatePhaseFlowEdges(phases: Phase[], flowDefinition: Array<[string, string]>): {
  id: string;
  source: string;
  target: string;
  data: PhaseEdgeData;
}[] {
  return flowDefinition.map(([sourceId, targetId]) => {
    const sourcePhase = getPhaseById(sourceId, phases);
    const targetPhase = getPhaseById(targetId, phases);
    
    return {
      id: createPhaseEdgeId(sourceId, targetId),
      source: createPhaseNodeId(sourceId),
      target: createPhaseNodeId(targetId),
      data: {
        id: createPhaseEdgeId(sourceId, targetId),
        color: sourcePhase?.color || "#cccccc",
        type: 'phase-flow' as PhaseEdgeType,
        strength: 'medium' as EdgeStrength
      }
    };
  }).filter(edge => edge.source && edge.target);
}